import React, { useMemo, useState, useEffect } from 'react';
import { FilterOptions, DepartmentDashboardMetrics } from '../types';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    PieChart, Pie, Cell, LineChart, Line, ResponsiveContainer, LabelList
} from 'recharts';
import {
    Users, TrendingUp, Download, Star, Award,
    Grid3X3, BarChart2, Trophy, AlertCircle,
    ArrowLeft, LogOut, Building2, Calendar, Filter, RefreshCw
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Footer from './Footer';
import { getWorkshopName, getDepartmentName } from '../utils/analytics';
import apiClient from '../utils/api';
import LoadingOverlay from './LoadingOverlay';
import { PortalLogoLink } from './PortalLogoLink';
import { useCache } from '../contexts/CacheContext';

// Function to convert rating to color
const getRatingColor = (rating: number) => {
    const normalized = (rating - 1) / 4;
    const red = Math.round(34 + (239 - 34) * (1 - normalized));
    const green = Math.round(197 * normalized + 68 * (1 - normalized));
    const blue = Math.round(94 * normalized + 68 * (1 - normalized));
    return `rgb(${red}, ${green}, ${blue})`;
};

// Custom Tooltip for charts
const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        return (
            <div className="bg-white p-4 rounded-lg shadow-xl border border-slate-200 backdrop-blur-sm">
                <p className="font-semibold text-slate-800 mb-2">{label}</p>
                {payload.map((entry: any, index: number) => (
                    <p key={index} className="text-sm flex items-center gap-2" style={{ color: entry.color }}>
                        <span className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }}></span>
                        {`${entry.name}: ${typeof entry.value === 'number' ? entry.value.toFixed(2) : entry.value}`}
                    </p>
                ))}
            </div>
        );
    }
    return null;
};

// Animated Progress Bar component
const ProgressBar = ({ label, yesPercent, delay = 0 }: any) => {
    const [animated, setAnimated] = useState(false);
    const noPercent = 100 - (yesPercent || 0);

    useEffect(() => {
        const timer = setTimeout(() => setAnimated(true), delay);
        return () => clearTimeout(timer);
    }, [delay]);

    return (
        <div className="text-sm">
            <div className="flex justify-between items-center mb-2">
                <span className="text-slate-600 font-medium">{label}</span>
                <div className="flex gap-4 font-bold text-xs">
                    <span className="text-green-600">{(yesPercent || 0).toFixed(0)}% Yes</span>
                    <span className="text-red-500">{noPercent.toFixed(0)}% No</span>
                </div>
            </div>
            <div className="flex h-3 w-full bg-red-200 rounded-full overflow-hidden shadow-inner">
                <div
                    style={{
                        width: animated ? `${yesPercent || 0}%` : '0%',
                        transition: 'width 1.5s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                    className="bg-gradient-to-r from-green-400 to-green-600 rounded-full shadow-sm"
                />
            </div>
        </div>
    );
};

// Department Performance Card
const DepartmentPerformanceCard = ({ department, data, isSelected, onClick }: any) => {
    const avgRating = data.avgOverallRating;
    const total = data.total;
    const components = data.components;

    return (
        <div
            onClick={() => onClick(department)}
            className={`p-4 rounded-xl border cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-1 ${isSelected
                ? 'border-blue-500 bg-blue-50 shadow-md ring-2 ring-blue-200'
                : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
        >
            <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${isSelected ? 'bg-blue-200' : 'bg-slate-100'}`}>
                        <Building2 className={`h-5 w-5 ${isSelected ? 'text-blue-700' : 'text-slate-600'}`} />
                    </div>
                    <div>
                        <h4 className="font-semibold text-slate-800 text-sm leading-tight">{getDepartmentName(department)}</h4>
                        <p className="text-xs text-slate-500 mt-1">{components} workshop{components !== 1 ? 's' : ''}</p>
                    </div>
                </div>
                <div className="flex items-center gap-1">
                    <Star size={14} fill="currentColor" className="text-amber-400" />
                    <span className="font-bold text-lg text-slate-800">{Number(avgRating || 0).toFixed(2)}</span>
                </div>
            </div>

            <div className="flex justify-between items-center text-xs text-slate-600">
                <span>{total} responses</span>
                <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: getRatingColor(avgRating) }}
                    title={`Rating: ${Number(avgRating || 0).toFixed(2)}/5`}
                ></div>
            </div>
        </div>
    );
};

interface DepartmentDashboardProps {
    filters: FilterOptions;
    onFiltersChange: (filters: FilterOptions) => void;
    onLogout: () => void;
}

const DepartmentDashboard: React.FC<DepartmentDashboardProps> = ({ filters, onFiltersChange, onLogout }) => {
    const navigate = useNavigate();
    const { lastInvalidated, refreshDashboard } = useCache();
    const [selectedDepartment, setSelectedDepartment] = useState<string | null>(null);
    const [departmentViewMode, setDepartmentViewMode] = useState<'cards' | 'chart'>('cards');
    const [pendingDateRange, setPendingDateRange] = useState(filters.dateRange);
    const [metrics, setMetrics] = useState<DepartmentDashboardMetrics | null>(null);
    const [loadingMetrics, setLoadingMetrics] = useState<boolean>(false);
    const [exporting, setExporting] = useState<boolean>(false);

    useEffect(() => {
        setPendingDateRange({ start: filters.dateRange.start, end: filters.dateRange.end });
    }, [filters.dateRange.start, filters.dateRange.end]);

    const hasPendingChanges = useMemo(() => {
        return pendingDateRange.start !== filters.dateRange.start || pendingDateRange.end !== filters.dateRange.end;
    }, [pendingDateRange, filters.dateRange.start, filters.dateRange.end]);

    // Fetch computed department dashboard metrics (date-range only)
    useEffect(() => {
        let cancelled = false;
        setLoadingMetrics(true);

        (async () => {
            try {
                const m = await apiClient.getDepartmentDashboardMetrics({
                    startDate: filters.dateRange.start || undefined,
                    endDate: filters.dateRange.end || undefined,
                });
                if (!cancelled) setMetrics(m);
            } catch {
                if (!cancelled) setMetrics(null);
            } finally {
                if (!cancelled) setLoadingMetrics(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [filters.dateRange.start, filters.dateRange.end, lastInvalidated]); // Add lastInvalidated as dependency

    // Handle department click
    const handleDepartmentClick = (department: string) => {
        if (selectedDepartment === department) {
            setSelectedDepartment(null);
        } else {
            setSelectedDepartment(department);
        }
    };

    const departmentBreakdown = useMemo(() => {
        const list = metrics?.departmentBreakdown || [];
        return list.map(d => ({
            department: d.departmentId,
            total: d.totalResponses,
            avgOverallRating: d.avgOverallRating,
            components: d.components,
            districts: d.districts,
            q1Percentage: d.q1Percentage,
            q2Percentage: d.q2Percentage,
            q3Percentage: d.q3Percentage,
            q4Percentage: d.q4Percentage,
            q5Percentage: d.q5Percentage,
            participants: d.participants,
        })).sort((a: any, b: any) => b.avgOverallRating - a.avgOverallRating);
    }, [metrics]);

    // Export filtered data to CSV
    const exportToCSV = async () => {
        setExporting(true);
        // Helper function to escape CSV values
        const escapeCSV = (value: string | number): string => {
            const str = String(value);
            // If value contains comma, quote, or newline, wrap in quotes and escape internal quotes
            if (str.includes(',') || str.includes('"') || str.includes('\n')) {
                return `"${str.replace(/"/g, '""')}"`;
            }
            // For text fields that might have spaces or special chars, wrap in quotes for consistency
            return str;
        };

        const headers = [
            'Candidate Name', 'Mobile', 'District', 'Workshop', 'Implementation Partner',
            'Batch ID', 'Start Date', 'End Date', 'Response Date',
            'Q1 (Content Easy)', 'Q2 (Added Value)', 'Q3 (Interaction)',
            'Q4 (Arrangements)', 'Q5 (Recommend)', 'Rating'
        ];

        try {
            const filteredData = await apiClient.getSurveyResponses({
                startDate: filters.dateRange.start || undefined,
                endDate: filters.dateRange.end || undefined,
            });

            const csvContent = [
                headers.join(','),
                ...filteredData.map(item => [
                    escapeCSV(item.candidateName),
                    escapeCSV(item.candidateMobile),
                    escapeCSV(item.district),
                    escapeCSV(getWorkshopName(item.workshopId)),
                    escapeCSV(getDepartmentName(item.implementationPartnerId)),
                    escapeCSV(item.batchId),
                    escapeCSV(item.workshopStartDate),
                    escapeCSV(item.workshopEndDate),
                    escapeCSV(item.responseTimestamp),
                    escapeCSV(item.response.q1),
                    escapeCSV(item.response.q2),
                    escapeCSV(item.response.q3),
                    escapeCSV(item.response.q4),
                    escapeCSV(item.response.q5),
                    escapeCSV(item.response.rating)
                ].join(','))
            ].join('\n');

            const blob = new Blob([csvContent], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;

            // Generate filename with current date and filter info
            const dateStr = new Date().toISOString().split('T')[0];
            let filename = `msme-department-data-${dateStr}`;
            if (filters.districts.length > 0) {
                filename += `-${filters.districts.length}districts`;
            }
            if (filters.workshops.length > 0) {
                filename += `-${filters.workshops.length}workshops`;
            }
            if (filters.implementationPartners.length > 0) {
                filename += `-${filters.implementationPartners.length}departments`;
            }
            filename += '.csv';

            a.download = filename;
            a.click();
            window.URL.revokeObjectURL(url);
        } finally {
            setExporting(false);
        }
    };

    // Workshop breakdown for selected department
    const workshopBreakdown = useMemo(() => {
        if (!selectedDepartment) return [];
        const list = metrics?.workshopBreakdownByDepartment?.[selectedDepartment] || [];
        return list.map(w => ({
            workshopId: w.workshopId,
            workshopName: getWorkshopName(w.workshopId),
            total: w.total,
            avgOverallRating: w.avgOverallRating,
            enrolledCount: 0,
            q1Percentage: w.q1Percentage,
            q2Percentage: w.q2Percentage,
            q3Percentage: w.q3Percentage,
            q4Percentage: w.q4Percentage,
            q5Percentage: w.q5Percentage,
        })).sort((a: any, b: any) => b.avgOverallRating - a.avgOverallRating);
    }, [metrics, selectedDepartment]);

    // Analytics calculations
    const analytics = useMemo(() => {
        if (!metrics) {
            return {
                total: 0,
                totalDepartments: 0,
                avgDepartmentRating: '0.0',
                topDepartmentRating: '0.0',
                departmentsAbove4: 0,
                departmentsBelow3: 0
            };
        }
        return {
            total: metrics.kpis.totalResponses,
            totalDepartments: metrics.kpis.totalDepartments,
            avgDepartmentRating: metrics.kpis.avgDepartmentRating.toFixed(2),
            topDepartmentRating: metrics.kpis.topDepartmentRating.toFixed(2),
            departmentsAbove4: metrics.kpis.departmentsAbove4,
            departmentsBelow3: metrics.kpis.departmentsBelow3
        };
    }, [metrics]);

    // Weekly trend by department
    const weeklyTrendByDepartment = useMemo(() => {
        const points = metrics?.weeklyTrend || [];
        return points.map(p => ({ date: p.date, timestamp: p.timestamp, ...p.values }));
    }, [metrics]);

    // Unified colors for charts
    const DEPARTMENT_COLORS = [
        '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
        '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#6366f1'
    ];

    const UNIFIED_COLORS = ['#dc2626', '#ea580c', '#d97706', '#16a34a', '#059669'];

    // Label renderers
    const RADIAN = Math.PI / 180;
    const renderPercentLabelOutside = ({ cx, cy, midAngle, outerRadius, percent }: any) => {
        if (percent < 0.07) return null;
        const radius = outerRadius + 20;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);
        const textAnchor = x > cx ? 'start' : 'end';

        return (
            <text
                x={x}
                y={y}
                fill="#475569"
                fontSize={12}
                fontWeight="700"
                textAnchor={textAnchor}
                dominantBaseline="central"
            >
                {`${(percent * 100).toFixed(0)}%`}
            </text>
        );
    };

    return (
        <div className="min-h-screen bg-slate-100 p-2 md:p-4 font-sans flex flex-col" aria-busy={loadingMetrics || exporting}>
            <LoadingOverlay
                show={loadingMetrics || exporting}
                label={exporting ? 'Preparing export…' : 'Loading department analytics…'}
            />
            {/* Enhanced Header */}
            <header className="mb-4">
                <div className="bg-white rounded-xl shadow-sm p-4 border border-slate-200 transition-all duration-300 hover:shadow-md">
                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-2">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => navigate('/feedback-dashboard')}
                                className="p-2 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                                title="Back to Feedback Dashboard"
                            >
                                <ArrowLeft size={20} className="text-slate-600" />
                            </button>
                            <div className="flex items-center gap-3">
                                <PortalLogoLink />
                            </div>
                            <div>
                                <h1 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
                                    Department Analytics
                                </h1>
                                <p className="text-slate-500 mt-1 text-sm">
                                    ANOs & DIOs Performance Dashboard
                                </p>
                            </div>
                        </div>
                        <div className="flex gap-2 w-full md:w-auto">
                            <button
                                onClick={exportToCSV}
                                disabled={exporting || loadingMetrics}
                                className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white px-4 py-2 rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all duration-200 shadow-sm hover:shadow-md text-sm flex-1 md:flex-none"
                            >
                                <Download size={16} />
                                {exporting ? 'Exporting…' : 'Export Data'}
                            </button>
                            <button
                                onClick={refreshDashboard}
                                disabled={loadingMetrics}
                                className="flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white px-4 py-2 rounded-lg hover:from-indigo-700 hover:to-indigo-800 transition-all duration-200 shadow-sm hover:shadow-md text-sm flex-1 md:flex-none disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <RefreshCw size={16} className={loadingMetrics ? 'animate-spin' : ''} />
                                {loadingMetrics ? 'Refreshing...' : 'Refresh Data'}
                            </button>
                            <button
                                onClick={onLogout}
                                className="flex items-center gap-2 bg-gradient-to-r from-red-600 to-red-700 text-white px-4 py-2 rounded-lg hover:from-red-700 hover:to-red-800 transition-all duration-200 shadow-sm hover:shadow-md text-sm flex-1 md:flex-none"
                            >
                                <LogOut size={16} />
                                Logout
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Date Range Filter (only) */}
            <section className="mb-4">
                <div className="bg-white rounded-xl shadow-sm p-4 border border-slate-200 transition-all duration-300 hover:shadow-md">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 bg-slate-100 rounded-lg">
                            <Filter size={18} className="text-slate-600" />
                        </div>
                        <h2 className="text-lg font-semibold text-slate-700">Filters</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <label className="flex text-sm font-medium text-slate-700 items-center gap-2">
                                <Calendar className="w-4 h-4" />
                                Date Range (Workshop End Date)
                            </label>
                            <div className="grid grid-cols-2 gap-2">
                                <input
                                    type="date"
                                    value={pendingDateRange.start}
                                    onChange={(e) => setPendingDateRange({ ...pendingDateRange, start: e.target.value })}
                                    className="w-full px-3 py-2 border border-slate-200 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                                    placeholder="Start Date"
                                />
                                <input
                                    type="date"
                                    value={pendingDateRange.end}
                                    onChange={(e) => setPendingDateRange({ ...pendingDateRange, end: e.target.value })}
                                    className="w-full px-3 py-2 border border-slate-200 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                                    placeholder="End Date"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="mt-3 flex flex-col sm:flex-row gap-2 justify-end">
                        <button
                            onClick={() => {
                                const cleared = { start: '', end: '' };
                                setPendingDateRange(cleared);
                                onFiltersChange({
                                    workshops: [],
                                    districts: [],
                                    implementationPartners: [],
                                    dateRange: cleared
                                });
                            }}
                            disabled={!pendingDateRange.start && !pendingDateRange.end && !hasPendingChanges}
                            className="px-4 py-2 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold"
                        >
                            Reset Filters
                        </button>
                        <button
                            onClick={() =>
                                onFiltersChange({
                                    workshops: [],
                                    districts: [],
                                    implementationPartners: [],
                                    dateRange: pendingDateRange
                                })
                            }
                            disabled={!hasPendingChanges || loadingMetrics}
                            className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#0D52BD] to-[#0a3d8f] text-white hover:from-[#0a3d8f] hover:to-blue-900 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold shadow-sm hover:shadow-md"
                        >
                            {loadingMetrics ? 'Loading…' : 'Apply Filters'}
                        </button>
                    </div>
                </div>
            </section>

            {/* Enhanced KPI Cards */}
            <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 mb-4">
                <div className="bg-white rounded-xl shadow-sm p-4 flex items-center justify-between border border-slate-200 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                    <div>
                        <p className="text-sm text-slate-500 font-medium">Total Departments</p>
                        <p className="text-2xl font-bold text-slate-800">
                            {analytics.totalDepartments}
                        </p>
                    </div>
                    <div className="bg-purple-100 p-3 rounded-full">
                        <Building2 className="h-6 w-6 text-purple-600" />
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm p-4 flex items-center justify-between border border-slate-200 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                    <div>
                        <p className="text-sm text-slate-500 font-medium">Total Responses</p>
                        <p className="text-2xl font-bold text-slate-800">
                            {analytics.total.toLocaleString()}
                        </p>
                    </div>
                    <div className="bg-blue-100 p-3 rounded-full">
                        <Users className="h-6 w-6 text-blue-600" />
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm p-4 flex items-center justify-between border border-slate-200 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                    <div>
                        <p className="text-sm text-slate-500 font-medium">Avg Department Rating</p>
                        <p className="text-2xl font-bold text-yellow-500">
                            {analytics.avgDepartmentRating}/5
                        </p>
                    </div>
                    <div className="bg-yellow-100 p-3 rounded-full">
                        <Star className="h-6 w-6 text-yellow-500" />
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm p-4 flex items-center justify-between border border-slate-200 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                    <div>
                        <p className="text-sm text-slate-500 font-medium">Top Department Rating</p>
                        <p className="text-2xl font-bold text-green-600">
                            {analytics.topDepartmentRating}/5
                        </p>
                    </div>
                    <div className="bg-green-100 p-3 rounded-full">
                        <Trophy className="h-6 w-6 text-green-600" />
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm p-4 flex items-center justify-between border border-slate-200 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                    <div>
                        <p className="text-sm text-slate-500 font-medium">Departments ≥ 4.0</p>
                        <p className="text-2xl font-bold text-indigo-600">
                            {analytics.departmentsAbove4}
                        </p>
                    </div>
                    <div className="bg-indigo-100 p-3 rounded-full">
                        <TrendingUp className="h-6 w-6 text-indigo-600" />
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm p-4 flex items-center justify-between border border-slate-200 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                    <div>
                        <p className="text-sm text-slate-500 font-medium">{'Departments < 3.0'}</p>
                        <p className="text-2xl font-bold text-red-600">
                            {analytics.departmentsBelow3}
                        </p>
                    </div>
                    <div className="bg-red-100 p-3 rounded-full">
                        <AlertCircle className="h-6 w-6 text-red-600" />
                    </div>
                </div>
            </section>

            {/* Department Overview Section */}
            <section className="mb-4">
                <div className="bg-white rounded-xl shadow-sm p-4 border border-slate-200 transition-all duration-300 hover:shadow-md">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-purple-100 rounded-lg">
                                <Building2 className="h-5 w-5 text-purple-600" />
                            </div>
                            <h3 className="text-lg font-semibold text-slate-700">Department Performance Overview</h3>
                        </div>

                        {/* View Mode Toggle */}
                        <div className="bg-slate-100 rounded-lg p-1 border border-slate-200 shadow-sm">
                            <div className="flex items-center">
                                <button
                                    onClick={() => setDepartmentViewMode('cards')}
                                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${departmentViewMode === 'cards'
                                        ? 'bg-white text-slate-900 shadow-sm border border-slate-200'
                                        : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
                                        }`}
                                >
                                    <Grid3X3 size={16} />
                                    Cards
                                </button>
                                <button
                                    onClick={() => setDepartmentViewMode('chart')}
                                    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200 ${departmentViewMode === 'chart'
                                        ? 'bg-white text-slate-900 shadow-sm border border-slate-200'
                                        : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
                                        }`}
                                >
                                    <BarChart2 size={16} />
                                    Chart
                                </button>
                            </div>
                        </div>
                    </div>

                    {departmentViewMode === 'cards' ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {departmentBreakdown.map((department: any) => (
                                <DepartmentPerformanceCard
                                    key={department.department}
                                    department={department.department}
                                    data={department}
                                    isSelected={selectedDepartment === department.department}
                                    onClick={handleDepartmentClick}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="w-full">
                            <ResponsiveContainer width="100%" height={400}>
                                <BarChart
                                    data={departmentBreakdown}
                                    margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.7} />
                                    <XAxis
                                        dataKey="department"
                                        tick={{ fontSize: 10, fill: '#64748b' }}
                                        angle={-45}
                                        textAnchor="end"
                                        height={100}
                                        interval={0}
                                    />
                                    <YAxis
                                        tick={{ fontSize: 11, fill: '#64748b' }}
                                        domain={[0, 5]}
                                    />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Bar
                                        dataKey="avgOverallRating"
                                        name="Average Rating"
                                        radius={[4, 4, 0, 0]}
                                        cursor="pointer"
                                        onClick={(data: any) => handleDepartmentClick(data.department)}
                                    >
                                        <LabelList
                                            dataKey="avgOverallRating"
                                            position="top"
                                            style={{ fill: '#1f2937', fontSize: 11, fontWeight: 'bold' }}
                                        />
                                        {departmentBreakdown.map((entry: any, index: number) => (
                                            <Cell
                                                key={`cell-department-${index}`}
                                                fill={getRatingColor(entry.avgOverallRating)}
                                                className={`transition-all duration-200 hover:brightness-110 ${selectedDepartment === entry.department ? 'brightness-90 stroke-purple-500 stroke-2' : ''
                                                    }`}
                                            />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                            <p className="mt-2 text-xs text-slate-500 italic text-center">Click on any department bar to filter data for that department</p>
                        </div>
                    )}
                </div>
            </section>

            {/* Main Content Grid */}
            <main className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Left Column - 2/3 width */}
                <div className="lg:col-span-2 space-y-4">

                    {/* Department Performance Details */}
                    <section className="bg-white rounded-xl shadow-sm p-4 border border-slate-200 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="p-2 bg-blue-100 rounded-lg">
                                <BarChart2 className="h-5 w-5 text-blue-600" />
                            </div>
                            <h3 className="text-lg font-semibold text-slate-700">Detailed Performance Metrics</h3>
                        </div>

                        {selectedDepartment ? (
                            // Selected Department Details
                            <div>
                                <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 mb-6 border border-purple-200">
                                    <h4 className="font-bold text-lg text-slate-800 mb-2">{getDepartmentName(selectedDepartment)}</h4>
                                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                                        <div>
                                            <p className="text-slate-600">Workshops</p>
                                            <p className="font-bold text-slate-800">{departmentBreakdown.find((d: any) => d.department === selectedDepartment)?.components || 0}</p>
                                        </div>
                                        <div>
                                            <p className="text-slate-600">Districts</p>
                                            <p className="font-bold text-slate-800">{departmentBreakdown.find((d: any) => d.department === selectedDepartment)?.districts || 0}</p>
                                        </div>
                                        <div>
                                            <p className="text-slate-600">Total Participants</p>
                                            <p className="font-bold text-slate-800">
                                                {(departmentBreakdown.find((d: any) => d.department === selectedDepartment)?.participants ?? 0).toLocaleString()}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-slate-600">Responses</p>
                                            <p className="font-bold text-slate-800">{departmentBreakdown.find((d: any) => d.department === selectedDepartment)?.total || 0}</p>
                                        </div>
                                        <div>
                                            <p className="text-slate-600">Avg Rating</p>
                                            <p className="font-bold text-yellow-600">{Number(departmentBreakdown.find((d: any) => d.department === selectedDepartment)?.avgOverallRating || 0).toFixed(2)}/5</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Progress bars for selected department */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {(() => {
                                        const department = departmentBreakdown.find((d: any) => d.department === selectedDepartment);
                                        if (!department) return null;

                                        return (
                                            <>
                                                <ProgressBar
                                                    label="Content Easy to Understand"
                                                    yesPercent={department.q1Percentage}
                                                    delay={100}
                                                />
                                                <ProgressBar
                                                    label="Added Value to Enterprise"
                                                    yesPercent={department.q2Percentage}
                                                    delay={200}
                                                />
                                                <ProgressBar
                                                    label="Encouraged Participant Interaction"
                                                    yesPercent={department.q3Percentage}
                                                    delay={300}
                                                />
                                                <ProgressBar
                                                    label="Arrangements Satisfactory"
                                                    yesPercent={department.q4Percentage}
                                                    delay={400}
                                                />
                                                <ProgressBar
                                                    label="Would Recommend"
                                                    yesPercent={department.q5Percentage}
                                                    delay={500}
                                                />
                                            </>
                                        );
                                    })()}
                                </div>

                                {/* Workshop-wise Performance Metrics */}
                                {workshopBreakdown.length > 0 && (
                                    <div className="mt-8">
                                        <div className="flex items-center gap-3 mb-6">
                                            <div className="p-2 bg-orange-100 rounded-lg">
                                                <BarChart2 className="h-5 w-5 text-orange-600" />
                                            </div>
                                            <h3 className="text-lg font-semibold text-slate-700">Workshop-wise Performance Metrics</h3>
                                        </div>

                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                            {workshopBreakdown.map((workshop: any, index: number) => (
                                                <div key={workshop.workshopId} className="bg-slate-50 rounded-lg p-4 border border-slate-200 hover:shadow-md transition-all duration-200">
                                                    <div className="flex items-center justify-between mb-4">
                                                        <div>
                                                            <h4 className="font-bold text-slate-800 text-sm">{workshop.workshopName}</h4>
                                                            <p className="text-xs text-slate-500 mt-1">Workshop ID: {workshop.workshopId}</p>
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            <Star size={16} fill="currentColor" className="text-amber-400" />
                                                            <span className="font-bold text-lg" style={{ color: getRatingColor(workshop.avgOverallRating) }}>
                                                                {workshop.avgOverallRating}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <div className="grid grid-cols-1 gap-3 mb-4 text-xs">
                                                        <div className="text-center p-2 bg-white rounded border">
                                                            <p className="text-slate-600">Responses</p>
                                                            <p className="font-bold text-slate-800">{workshop.total}</p>
                                                        </div>
                                                    </div>

                                                    <div className="space-y-2">
                                                        <ProgressBar
                                                            label="Content Easy to Understand"
                                                            yesPercent={workshop.q1Percentage}
                                                            delay={100 + index * 100}
                                                        />
                                                        <ProgressBar
                                                            label="Added Value to Enterprise"
                                                            yesPercent={workshop.q2Percentage}
                                                            delay={200 + index * 100}
                                                        />
                                                        <ProgressBar
                                                            label="Encouraged Participant Interaction"
                                                            yesPercent={workshop.q3Percentage}
                                                            delay={300 + index * 100}
                                                        />
                                                        <ProgressBar
                                                            label="Arrangements Satisfactory"
                                                            yesPercent={workshop.q4Percentage}
                                                            delay={400 + index * 100}
                                                        />
                                                        <ProgressBar
                                                            label="Would Recommend"
                                                            yesPercent={workshop.q5Percentage}
                                                            delay={500 + index * 100}
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            // All Departments Overview Table
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-slate-200">
                                    <thead className="bg-slate-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                                                Department
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                                                Number of Participants
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                                                Workshops
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                                                Districts
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                                                Responses
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                                                Avg Rating
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                                                Added Value %
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-slate-200">
                                        {departmentBreakdown.map((department: any) => (
                                            <tr
                                                key={department.department}
                                                className="hover:bg-slate-50 transition-colors cursor-pointer"
                                                onClick={() => handleDepartmentClick(department.department)}
                                            >
                                                <td className="px-4 py-4 text-sm font-medium text-slate-900">
                                                    <div className="flex items-center gap-2">
                                                        <div
                                                            className="w-3 h-3 rounded-full"
                                                            style={{ backgroundColor: getRatingColor(department.avgOverallRating) }}
                                                        />
                                                        {getDepartmentName(department.department)}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-4 text-sm text-slate-500">
                                                    {(department.participants ?? 0).toLocaleString()}
                                                </td>
                                                <td className="px-4 py-4 text-sm text-slate-500">
                                                    {department.components}
                                                </td>
                                                <td className="px-4 py-4 text-sm text-slate-500">
                                                    {department.districts}
                                                </td>
                                                <td className="px-4 py-4 text-sm text-slate-500">
                                                    {department.total.toLocaleString()}
                                                </td>
                                                <td className="px-4 py-4 text-sm font-semibold text-yellow-600">
                                                    {Number(department.avgOverallRating || 0).toFixed(2)}/5
                                                </td>
                                                <td className="px-4 py-4 text-sm text-green-600 font-medium">
                                                    {department.q2Percentage.toFixed(1)}%
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>

                </div>

                {/* Right Column - 1/3 width */}
                <div className="space-y-4">

                    {/* Weekly Trend */}
                    <section className="bg-white rounded-xl shadow-sm p-4 border border-slate-200 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-green-100 rounded-lg">
                                    <TrendingUp className="h-5 w-5 text-green-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-slate-700">
                                    {selectedDepartment ? 'Weekly Trend' : 'Top 3 Departments Weekly Trend'}
                                </h3>
                            </div>
                            <div className="text-sm text-slate-500 font-medium bg-slate-50 px-3 py-1 rounded-full">
                                Last 12 weeks
                            </div>
                        </div>

                        <ResponsiveContainer width="100%" height={280}>
                            <LineChart
                                data={weeklyTrendByDepartment}
                                margin={{ top: 5, right: 20, left: -10, bottom: 5 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.7} />
                                <XAxis
                                    dataKey="date"
                                    tick={{ fontSize: 10, fill: '#64748b' }}
                                    interval="preserveStartEnd"
                                />
                                <YAxis
                                    domain={[1, 5]}
                                    tick={{ fontSize: 11, fill: '#64748b' }}
                                />
                                <Tooltip content={<CustomTooltip />} />
                                {selectedDepartment ? (
                                    <Line
                                        type="monotone"
                                        dataKey={selectedDepartment}
                                        stroke="#8b5cf6"
                                        strokeWidth={3}
                                        dot={{ r: 4, fill: '#8b5cf6' }}
                                        name={getDepartmentName(selectedDepartment)}
                                    />
                                ) : (
                                    departmentBreakdown.slice(0, 3).map((department: any, index: number) => (
                                        <Line
                                            key={department.department}
                                            type="monotone"
                                            dataKey={department.department}
                                            stroke={DEPARTMENT_COLORS[index]}
                                            strokeWidth={2}
                                            dot={{ r: 3, fill: DEPARTMENT_COLORS[index] }}
                                            name={getDepartmentName(department.department)}
                                        />
                                    ))
                                )}
                                <Legend wrapperStyle={{ fontSize: '11px' }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </section>

                    {/* Rating Distribution */}
                    {selectedDepartment && (
                        <section className="bg-white rounded-xl shadow-sm p-4 border border-slate-200 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 bg-yellow-100 rounded-lg">
                                    <Star className="h-5 w-5 text-yellow-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-slate-700">Overall Rating Distribution</h3>
                            </div>

                            <ResponsiveContainer width="100%" height={280}>
                                <PieChart>
                                    <Pie
                                        data={(selectedDepartment && metrics?.ratingDistributionByDepartment?.[selectedDepartment])
                                            ? metrics.ratingDistributionByDepartment[selectedDepartment]
                                            : [1, 2, 3, 4, 5].map(rating => ({ name: `${rating} Star`, value: 0 }))}
                                        dataKey="value"
                                        nameKey="name"
                                        cx="50%"
                                        cy="50%"
                                        outerRadius={80}
                                        paddingAngle={2}
                                        label={renderPercentLabelOutside}
                                    >
                                        {UNIFIED_COLORS.map((color, index) => (
                                            <Cell key={`cell-${index}`} fill={color} />
                                        ))}
                                    </Pie>
                                    <Tooltip content={<CustomTooltip />} />
                                </PieChart>
                            </ResponsiveContainer>
                        </section>
                    )}

                    {/* Top & Bottom Performers */}
                    <section className="bg-white rounded-xl shadow-sm p-4 border border-slate-200 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 bg-amber-100 rounded-lg">
                                <Award className="h-5 w-5 text-amber-600" />
                            </div>
                            <h3 className="text-lg font-semibold text-slate-700">Performance Ranking</h3>
                        </div>

                        <div className="space-y-6">
                            {/* Top Performers */}
                            <div>
                                <h4 className="font-bold text-slate-600 flex items-center gap-2 mb-3">
                                    <Trophy className="text-green-500" size={16} />
                                    Top 3 Departments
                                </h4>
                                <div className="space-y-2">
                                    {departmentBreakdown.slice(0, 3).map((department: any, index: number) => (
                                        <div
                                            key={department.department}
                                            className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200 hover:bg-green-100 transition-colors cursor-pointer"
                                            onClick={() => handleDepartmentClick(department.department)}
                                        >
                                            <div className="flex items-center gap-3">
                                                <span className="w-6 h-6 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                                                    {index + 1}
                                                </span>
                                                <div>
                                                    <p className="font-medium text-slate-800 text-sm">{getDepartmentName(department.department)}</p>
                                                    <p className="text-xs text-slate-500">{department.total} responses</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <Star size={14} fill="currentColor" className="text-amber-400" />
                                                <span className="font-bold text-slate-800">{Number(department.avgOverallRating || 0).toFixed(2)}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Bottom Performers */}
                            <div>
                                <h4 className="font-bold text-slate-600 flex items-center gap-2 mb-3">
                                    <AlertCircle className="text-red-500" size={16} />
                                    Needs Improvement
                                </h4>
                                <div className="space-y-2">
                                    {departmentBreakdown.slice(-3).reverse().map((department: any, index: number) => (
                                        <div
                                            key={department.department}
                                            className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200 hover:bg-red-100 transition-colors cursor-pointer"
                                            onClick={() => handleDepartmentClick(department.department)}
                                        >
                                            <div className="flex items-center gap-3">
                                                <span className="w-6 h-6 bg-red-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                                                    {departmentBreakdown.length - index}
                                                </span>
                                                <div>
                                                    <p className="font-medium text-slate-800 text-sm">{getDepartmentName(department.department)}</p>
                                                    <p className="text-xs text-slate-500">{department.total} responses</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <Star size={14} fill="currentColor" className="text-amber-400" />
                                                <span className="font-bold text-slate-800">{Number(department.avgOverallRating || 0).toFixed(2)}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </main>

            {/* Footer */}
            <Footer />
        </div>
    );
};

export default DepartmentDashboard;
